/*
 * PDP Delivery Estimate
 *
 * Manual ZIP entry only (2026-10-08, Christophe: IP geolocation dropped as not worth the cost).
 *   No ZIP          -> "Estimated delivery: 3-7 business days" + "Enter ZIP for exact date"
 *   ZIP in table    -> "Delivering to {zip}" + "Delivery on or before {date}  Order by 2pm CT"
 *   ZIP not found   -> "Delivering to {zip}" + "Estimated delivery: 3-7 business days"
 * ZIP source, first match wins: a ZIP the shopper typed (remembered on the device in
 * localStorage) > the logged-in customer's default shipping address (data-customer-zip,
 * never written to localStorage, so a shared computer does not keep it) > none.
 *
 * A ZIP that is in neither the admin overrides nor the UPS table is reported once per
 * session as the event "delivery_zip_not_found": pushed to window.dataLayer (GTM/GA4)
 * and published through Shopify.analytics (readable by a Customer Events custom pixel).
 *
 * WHY THIS RUNS IN JAVASCRIPT AND NOT LIQUID
 * Shopify serves PDP HTML from a CDN cache. A date rendered from Liquid's `now`
 * would be frozen at cache time and go stale within hours. The ZIP is client-side
 * anyway, so the whole calculation lives here.
 *
 * WHY THE CLOCK IS AMERICA/CHICAGO AND NOT THE VISITOR'S
 * The 2pm cutoff is a warehouse cutoff in Plano TX. A shopper in Los Angeles at
 * 1:00pm local is already past it; one in New York at 2:30pm local is not.
 *
 * Data (transit table, holidays, cutoff) lives in delivery-transit-data.js.
 * Admin overrides (Content > Metaobjects: "Delivery ZIP override", "Delivery closed day")
 * are printed by the snippet as JSON in [data-de-overrides] and layered on top.
 */
(function () {
  'use strict';

  var DAY_MS = 86400000;
  var STORAGE_KEY = 'os_delivery_zip';
  var STORAGE_TTL_MS = 30 * DAY_MS;

  var US_STATES = ('AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD ' +
    'MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT ' +
    'VA WA WV WI WY DC').split(' ');

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
  var WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
    'Friday', 'Saturday'];

  function data() {
    return window.OS_DELIVERY_DATA || null;
  }

  /* ---------------------------------------------------------------- date math */

  /** Current wall-clock time in America/Chicago. Handles DST via Intl. */
  function nowCT() {
    var parts = {};
    try {
      new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date()).forEach(function (p) {
        parts[p.type] = p.value;
      });
      return {
        y: +parts.year, m: +parts.month, d: +parts.day,
        hour: +parts.hour, minute: +parts.minute
      };
    } catch (e) {
      // Intl or the tz database unavailable -- fall back to local time. The date
      // may be off by a day near the cutoff; better than rendering nothing.
      var n = new Date();
      return {
        y: n.getFullYear(), m: n.getMonth() + 1, d: n.getDate(),
        hour: n.getHours(), minute: n.getMinutes()
      };
    }
  }

  /* Arithmetic is done on UTC midnight timestamps so no local timezone or DST
     shift can move a date across a day boundary. */
  function toMs(y, m, d) { return Date.UTC(y, m - 1, d); }

  function iso(ms) {
    var d = new Date(ms);
    return d.getUTCFullYear() + '-' +
      ('0' + (d.getUTCMonth() + 1)).slice(-2) + '-' +
      ('0' + d.getUTCDate()).slice(-2);
  }

  /**
   * Business day = Mon-Fri and not a holiday.
   *
   * Saturday is deliberately NOT a business day. OneSource ships Mon-Fri, and the
   * UPS ZIP3 chart counts Mon-Fri transit. UPS may still *deliver* on a Saturday,
   * which lands the parcel earlier than calculated -- the "on or before" wording
   * in the copy covers that.
   *
   * Holidays are excluded here rather than added as "+1 day" afterwards. Skipping
   * them while counting produces the same result; doing both would double-count.
   */
  function isBusinessDay(ms, holidays) {
    var dow = new Date(ms).getUTCDay();
    if (dow === 0 || dow === 6) return false;
    return holidays.indexOf(iso(ms)) === -1;
  }

  function nextBusinessDay(ms, holidays) {
    do { ms += DAY_MS; } while (!isBusinessDay(ms, holidays));
    return ms;
  }

  /**
   * @returns {{deliveryMs:number, shipsToday:boolean, shipMs:number, todayMs:number}}
   */
  function calculate(transitDays, cfg, nowOverride) {
    var holidays = cfg.holidays || [];
    var now = nowOverride || nowCT();
    var todayMs = toMs(now.y, now.m, now.d);

    var canShipToday = isBusinessDay(todayMs, holidays) &&
      now.hour < (cfg.cutoffHourCT || 14);

    var shipMs = canShipToday ? todayMs : nextBusinessDay(todayMs, holidays);

    var ms = shipMs;
    var remaining = transitDays;
    while (remaining > 0) {
      ms += DAY_MS;
      if (isBusinessDay(ms, holidays)) remaining--;
    }

    return { deliveryMs: ms, shipsToday: canShipToday, shipMs: shipMs, todayMs: todayMs };
  }

  function formatDate(ms) {
    var d = new Date(ms);
    return WEEKDAYS[d.getUTCDay()] + ', ' + MONTHS[d.getUTCMonth()] + ' ' +
      d.getUTCDate();
  }

  /* ------------------------------------------------------------ zip + lookup */

  function normalizeZip(raw) {
    var m = String(raw || '').replace(/[^0-9]/g, '').slice(0, 5);
    return m.length === 5 ? m : null;
  }

  /**
   * Order: admin override for the full 5-digit ZIP, then for the 3-digit prefix, then the
   * UPS table. The state code always comes from the table (overrides carry days only), so
   * shipping restrictions still apply to overridden ZIPs.
   * @returns {{days:number, state:string, known:boolean, source:string}}
   *   known=false means "no data": the caller shows the 3-7 day range, not a date.
   */
  function lookupZip(zip, cfg, overrides) {
    var zip3 = zip.slice(0, 3);
    var entry = cfg.transit[zip3];
    var state = entry ? entry.slice(1) : '';
    var o = (overrides && overrides.zips) || {};
    if (o[zip]) return { days: o[zip], state: state, known: true, source: 'override5' };
    if (o[zip3]) return { days: o[zip3], state: state, known: true, source: 'override3' };
    if (entry) {
      return { days: parseInt(entry.charAt(0), 10), state: state, known: true, source: 'table' };
    }
    return { days: 0, state: '', known: false, source: 'none' };
  }

  /** Read the admin overrides the snippet prints as JSON. Never throws. */
  function readOverrides(root) {
    var out = { zips: {}, closed: [] };
    var node = root && root.querySelector('[data-de-overrides]');
    if (!node) return out;
    try {
      var j = JSON.parse(node.textContent || '{}');
      (j.zips || []).forEach(function (z) {
        var key = String(z.zip || '').replace(/[^0-9]/g, '');
        var days = parseInt(z.days, 10);
        if ((key.length === 3 || key.length === 5) && days > 0 && days <= 10) out.zips[key] = days;
      });
      (j.closed || []).forEach(function (d) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(d)) out.closed.push(d);
      });
    } catch (e) { /* malformed JSON: fall back to the built-in table and holidays */ }
    return out;
  }

  /** Built-in holidays plus admin closed days. */
  function mergedConfig(cfg, overrides) {
    var merged = {};
    for (var k in cfg) merged[k] = cfg[k];
    merged.holidays = (cfg.holidays || []).concat(overrides.closed || []);
    return merged;
  }

  /**
   * ZIP3s outside the 48 contiguous states are absent from the table by design.
   * They must NOT fall through to the 5-day estimate -- that would promise
   * delivery to an address that cannot be served.
   */
  function isUnservable(zip) {
    var n = parseInt(zip.slice(0, 3), 10);
    if (n >= 90 && n <= 99) return true;    // APO/FPO/DPO Europe
    if (n === 340) return true;             // APO/FPO Americas
    if (n >= 962 && n <= 969) return true;  // APO/FPO Pacific, Hawaii, Guam
    if (n >= 995 && n <= 999) return true;  // Alaska
    if (n >= 6 && n <= 9) return true;      // Puerto Rico
    return false;
  }

  /* --------------------------------------------------------- shipping groups */

  /** Parse "CA#Hazardous Products#MEDIUM" -> ["CA"] */
  function restrictedStates(shippingGroups) {
    if (!shippingGroups) return [];
    return shippingGroups.split('#')
      .map(function (t) { return t.trim().toUpperCase(); })
      .filter(function (t) { return US_STATES.indexOf(t) !== -1; });
  }

  /* -------------------------------------------------------------- persistence */

  function readStoredZip() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || !o.zip) return null;
      if (!o.manual && o.at && (Date.now() - o.at) > STORAGE_TTL_MS) return null;
      return o;
    } catch (e) { return null; }
  }

  function storeZip(zip, manual) {
    try {
      localStorage.setItem(STORAGE_KEY,
        JSON.stringify({ zip: zip, manual: !!manual, at: Date.now() }));
    } catch (e) { /* private mode -- the estimate still works for this page */ }
  }

  /* ------------------------------------------------------ unknown-ZIP reporting */

  function reportUnknownZip(zip) {
    var key = 'os_de_unknown_' + zip;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (e) { /* storage blocked: may report more than once, which is harmless */ }
    var payload = {
      zip: zip,
      zip3: zip.slice(0, 3),
      page: (window.location && window.location.pathname) || ''
    };
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'delivery_zip_not_found',
        delivery_zip: payload.zip,
        delivery_zip3: payload.zip3
      });
    } catch (e) { /* no dataLayer */ }
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish('delivery_zip_not_found', payload);
      }
    } catch (e) { /* analytics unavailable */ }
  }

  /* ------------------------------------------------------------------- render */

  function DeliveryEstimate(root) {
    this.root = root;
    this.overrides = readOverrides(root);
    this.cfg = data() ? mergedConfig(data(), this.overrides) : null;
    this.groups = root.getAttribute('data-shipping-groups') || '';
    this.cutoffText = root.getAttribute('data-cutoff-text') || 'Order by 2pm CT';
    this.customerZip = normalizeZip(root.getAttribute('data-customer-zip'));

    this.el = {
      summary: root.querySelector('[data-de-summary]'),
      zipLabel: root.querySelector('[data-de-zip]'),
      line: root.querySelector('[data-de-line]'),
      range: root.querySelector('[data-de-range]'),
      cutoff: root.querySelector('[data-de-cutoff]'),
      notice: root.querySelector('[data-de-notice]'),
      toggle: root.querySelector('[data-de-toggle]'),
      form: root.querySelector('[data-de-form]'),
      input: root.querySelector('[data-de-input]'),
      error: root.querySelector('[data-de-error]')
    };
  }

  DeliveryEstimate.prototype.setState = function (name) {
    this.root.setAttribute('data-state', name);
  };

  DeliveryEstimate.prototype.render = function (zip) {
    var cfg = this.cfg;
    if (!cfg || !zip) { this.setState('empty'); return; }

    this.el.zipLabel.textContent = zip;

    if (isUnservable(zip)) {
      this.el.notice.textContent = 'We ship to the 48 contiguous states.';
      this.setState('unservable');
      return;
    }

    var info = lookupZip(zip, cfg, this.overrides);

    var blocked = restrictedStates(this.groups);
    if (info.state && blocked.indexOf(info.state) !== -1) {
      this.el.notice.textContent = 'This item cannot be shipped to ' + info.state + '.';
      this.setState('restricted');
      return;
    }

    // No data for this ZIP: show the range rather than promise a date we cannot back up.
    if (!info.known) {
      reportUnknownZip(zip);
      this.setState('range');
      return;
    }

    var result = calculate(info.days, cfg);
    this.el.line.textContent = 'Delivery on or before ' + formatDate(result.deliveryMs);
    this.el.cutoff.textContent = cutoffLabel(this.cutoffText, result);
    this.el.cutoff.hidden = false;

    this.setState('ready');
  };

  /**
   * Before the cutoff on a ship day: "Order by 2pm CT". After it, the date already assumes
   * the next ship day, so the cutoff that achieves it is that day's: "... tomorrow" or
   * "... Monday" (after 2pm Friday, or before a holiday).
   */
  function cutoffLabel(text, result) {
    if (result.shipsToday) return text;
    if (result.shipMs - result.todayMs === DAY_MS) return text + ' tomorrow';
    return text + ' ' + WEEKDAYS[new Date(result.shipMs).getUTCDay()];
  }

  DeliveryEstimate.prototype.openForm = function (open) {
    this.el.form.hidden = !open;
    this.el.toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      this.el.input.value = this.el.zipLabel.textContent || '';
      this.el.input.focus();
      this.el.input.select();
    }
  };

  DeliveryEstimate.prototype.submit = function () {
    var zip = normalizeZip(this.el.input.value);
    if (!zip) {
      this.el.error.textContent = 'Enter a 5-digit ZIP code.';
      this.el.error.hidden = false;
      this.el.input.focus();
      return;
    }
    this.el.error.hidden = true;
    storeZip(zip, true);
    this.render(zip);
    this.openForm(false);
    this.el.toggle.focus();
  };

  DeliveryEstimate.prototype.init = function () {
    var self = this;
    if (!this.cfg || !this.el.summary) return;

    this.el.toggle.addEventListener('click', function () {
      self.openForm(self.el.form.hidden);
    });

    this.root.querySelector('[data-de-submit]')
      .addEventListener('click', function () { self.submit(); });

    this.el.input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); self.submit(); }
      if (e.key === 'Escape') { self.openForm(false); self.el.toggle.focus(); }
    });

    var stored = readStoredZip();
    this.render(stored ? stored.zip : this.customerZip);
  };

  function initAll() {
    var nodes = document.querySelectorAll('[data-delivery-estimate]');
    Array.prototype.forEach.call(nodes, function (n) {
      if (n.dataset.deReady === 'true') return;
      n.dataset.deReady = 'true';
      new DeliveryEstimate(n).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', initAll);

  // Exposed for the test harness in 04-deployment/.
  window.OSDeliveryEstimate = {
    calculate: calculate,
    formatDate: formatDate,
    lookupZip: lookupZip,
    cutoffLabel: cutoffLabel,
    reportUnknownZip: reportUnknownZip,
    mergedConfig: mergedConfig,
    isUnservable: isUnservable,
    restrictedStates: restrictedStates,
    normalizeZip: normalizeZip,
    isBusinessDay: isBusinessDay,
    toMs: toMs,
    iso: iso
  };
})();
